// Scoped CSS for the widget. Injected once into the shadow root, so class names
// need no prefixing — the shadow boundary isolates everything from the storefront.
// Brand color comes in as the --dts-accent custom property set on the host element.

export function buildStyles(): string {
  return `
:host {
  all: initial;
  --dts-accent: #9C0707;
  --dts-accent-text: #ffffff;
  --dts-bg: #ffffff;
  --dts-fg: #1f2937;
  --dts-muted: #6b7280;
  --dts-bot: #f1f5f9;
  --dts-border: #e5e7eb;
  --dts-radius: 16px;
  --dts-shadow: 0 12px 34px rgba(15, 23, 42, .22);
  --dts-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-family: var(--dts-font);
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}
*, *::before, *::after { box-sizing: border-box; }
button { font-family: inherit; }

/* ── Launcher bubble ─────────────────────────────── */
.launcher {
  position: fixed; bottom: 20px; right: 20px;
  width: 60px; height: 60px; border-radius: 50%;
  border: none; cursor: pointer;
  background: var(--dts-accent); color: var(--dts-accent-text);
  box-shadow: var(--dts-shadow);
  display: flex; align-items: center; justify-content: center;
  z-index: 2147483647; /* max — Smartsupp's off-hours force-shown launcher uses 2147483647; ours must not sit under it */
  transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease;
}
.launcher:hover { transform: scale(1.06); }
.launcher:active { transform: scale(.97); }
.launcher svg { width: 28px; height: 28px; }
.launcher.hidden { opacity: 0; pointer-events: none; transform: scale(.6); }

/* ── Panel ───────────────────────────────────────── */
.panel {
  position: fixed; bottom: 20px; right: 20px;
  width: 384px; max-width: calc(100vw - 32px);
  height: 600px; max-height: calc(100vh - 40px);
  background: var(--dts-bg); border-radius: var(--dts-radius);
  box-shadow: var(--dts-shadow);
  display: flex; flex-direction: column; overflow: hidden;
  z-index: 2147483647; /* max — Smartsupp's off-hours force-shown launcher uses 2147483647; ours must not sit under it */
  opacity: 0; transform: translateY(16px) scale(.98); pointer-events: none;
  transition: opacity .2s ease, transform .2s ease, width .2s, height .2s;
}
.panel.open { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
.panel.expanded { width: min(720px, calc(100vw - 32px)); height: min(85vh, 900px); }

/* ── Header ──────────────────────────────────────── */
.header {
  background: var(--dts-accent); color: var(--dts-accent-text);
  padding: 14px 16px; display: flex; align-items: center; gap: 12px;
}
.header .avatar {
  width: 34px; height: 34px; border-radius: 50%;
  background: rgba(255,255,255,.18);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.header .avatar svg { width: 19px; height: 19px; }
.header .meta { flex: 1; min-width: 0; }
.header .title { font-weight: 600; font-size: 15px; }
.header .status { font-size: 12px; opacity: .85; display: flex; align-items: center; gap: 5px; }
.header .dot { width: 7px; height: 7px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 0 2px rgba(74,222,128,.3); }
.header .close {
  background: transparent; border: none; color: inherit; cursor: pointer;
  padding: 5px; border-radius: 7px; opacity: .9; display: flex;
}
.header .close:hover { opacity: 1; background: rgba(255,255,255,.16); }
.header .close svg { width: 18px; height: 18px; }
.header .expand {
  background: transparent; border: none; color: inherit; cursor: pointer;
  padding: 5px; border-radius: 7px; opacity: .9; display: flex; flex-shrink: 0;
}
.header .expand:hover { opacity: 1; background: rgba(255,255,255,.16); }
.header .expand svg { width: 17px; height: 17px; }
.header .newchat {
  background: transparent; border: none; color: inherit; cursor: pointer;
  padding: 5px; border-radius: 7px; opacity: .9; display: flex; flex-shrink: 0;
}
.header .newchat:hover { opacity: 1; background: rgba(255,255,255,.16); }
.header .newchat svg { width: 17px; height: 17px; }
.header .human {
  background: rgba(255,255,255,.16); border: none; color: inherit; cursor: pointer;
  font-size: 12px; font-weight: 600; line-height: 1; white-space: nowrap; flex-shrink: 0;
  padding: 7px 11px; border-radius: 999px; transition: background .12s ease;
}
.header .human:hover { background: rgba(255,255,255,.28); }

/* ── Messages ────────────────────────────────────── */
.messages {
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
  background: #fafbfc; scroll-behavior: smooth;
}
.msg { display: flex; flex-direction: column; max-width: 86%; gap: 5px; }
.msg.user { align-self: flex-end; align-items: flex-end; }
.msg.bot { align-self: flex-start; align-items: flex-start; }
.bubble {
  padding: 10px 13px; border-radius: 15px; font-size: 14px;
  white-space: pre-wrap; word-wrap: break-word; overflow-wrap: anywhere;
}
.msg.user .bubble { background: var(--dts-accent); color: var(--dts-accent-text); border-bottom-right-radius: 4px; }
.msg.bot .bubble { background: var(--dts-bot); color: var(--dts-fg); border-bottom-left-radius: 4px; }
.msg.bot .bubble a { color: var(--dts-accent); text-decoration: underline; word-break: break-all; }
.msg.bot .bubble a:hover { text-decoration: none; }
.msg.system .bubble { background: #fff7ed; color: #9a3412; border: 1px solid #fed7aa; font-size: 13px; }

/* ── Sources ─────────────────────────────────────── */
.sources { display: flex; flex-direction: column; gap: 3px; padding-left: 2px; }
.sources .label { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--dts-muted); }
.sources a {
  font-size: 12px; color: var(--dts-accent); text-decoration: none;
  display: inline-flex; align-items: center; gap: 5px; max-width: 100%;
}
.sources a:hover { text-decoration: underline; }
.sources a svg { width: 11px; height: 11px; flex-shrink: 0; opacity: .7; }
.sources a span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Typing indicator ────────────────────────────── */
.attach-btn { background: none; border: 0; font-size: 17px; cursor: pointer; padding: 0 4px; line-height: 1; opacity: .65; }
.attach-btn:hover { opacity: 1; }
.bubble.attach { padding: 6px; }
.attach-img { max-width: 220px; max-height: 220px; border-radius: 10px; display: block; }
.attach-file { color: inherit; text-decoration: underline; word-break: break-all; }
.attach-size { font-size: 11px; opacity: .7; margin-top: 2px; }
.bubble.sys-error { background: #fdecec; color: #a01b1b; font-size: 13px; }
.msg.deleted .bubble.deleted-note { background: transparent; border: 1px dashed var(--dts-muted); color: var(--dts-muted); font-style: italic; font-size: 12px; }
.rating { margin: 10px 0; padding: 12px; border-radius: 12px; background: var(--dts-bot); text-align: center; }
.rating-q { font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.rating-row { display: flex; gap: 8px; justify-content: center; }
.rating-btn { border: 1px solid var(--dts-muted); background: #fff; border-radius: 999px; padding: 6px 14px; font-size: 13px; cursor: pointer; }
.rating-btn:hover { border-color: var(--dts-brand); }
.rating-comment { width: 100%; margin-top: 8px; border-radius: 8px; border: 1px solid var(--dts-muted); padding: 6px; font: inherit; font-size: 13px; box-sizing: border-box; }
.rating-send { margin-top: 6px; background: var(--dts-brand); color: #fff; border: 0; border-radius: 8px; padding: 6px 14px; font-size: 13px; cursor: pointer; }
.rating-thanks { font-size: 13px; }
.launcher.has-unread::after { content: ''; position: absolute; top: 2px; right: 2px; width: 11px; height: 11px; border-radius: 50%; background: #e03131; border: 2px solid #fff; }
.typing { display: flex; gap: 4px; padding: 12px 14px; background: var(--dts-bot); border-radius: 15px; border-bottom-left-radius: 4px; width: fit-content; }
.typing span { width: 7px; height: 7px; border-radius: 50%; background: var(--dts-muted); animation: dts-bounce 1.2s infinite ease-in-out; }
.typing span:nth-child(2) { animation-delay: .15s; }
.typing span:nth-child(3) { animation-delay: .3s; }
@keyframes dts-bounce { 0%, 80%, 100% { transform: scale(.55); opacity: .45; } 40% { transform: scale(1); opacity: 1; } }

/* ── Quick replies ───────────────────────────────── */
.quick { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 16px 12px; }
.quick.hidden { display: none; }
.quick button {
  font-size: 13px; padding: 8px 13px; border-radius: 999px;
  border: 1px solid var(--dts-accent); background: transparent; color: var(--dts-accent);
  cursor: pointer; transition: background .12s ease, color .12s ease;
}
.quick button:hover { background: var(--dts-accent); color: var(--dts-accent-text); }

/* ── Composer ────────────────────────────────────── */
.composer { display: flex; align-items: flex-end; gap: 8px; padding: 12px; border-top: 1px solid var(--dts-border); background: var(--dts-bg); }
.composer textarea {
  flex: 1; resize: none; font-size: 14px; line-height: 1.4;
  border: 1px solid var(--dts-border); border-radius: 12px; padding: 10px 12px;
  max-height: 104px; color: var(--dts-fg); background: #fff;
}
.composer textarea::placeholder { color: #9ca3af; }
.composer textarea:focus { outline: none; border-color: var(--dts-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--dts-accent) 18%, transparent); }
.composer .send {
  background: var(--dts-accent); color: var(--dts-accent-text); border: none; border-radius: 12px;
  width: 42px; height: 42px; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: opacity .12s ease, transform .12s ease;
}
.composer .send:hover:not(:disabled) { transform: translateY(-1px); }
.composer .send:disabled { opacity: .45; cursor: default; }
.composer .send svg { width: 18px; height: 18px; }

/* ── Escalation / email-capture form ─────────────── */
.escalate {
  background: #fff; border: 1px solid var(--dts-border); border-radius: 14px;
  padding: 14px; display: flex; flex-direction: column; gap: 9px; width: 100%;
}
.escalate .lead { font-size: 13px; color: var(--dts-fg); }
.escalate label { font-size: 11px; font-weight: 600; color: var(--dts-muted); text-transform: uppercase; letter-spacing: .04em; }
.escalate .field { display: flex; flex-direction: column; gap: 4px; }
.escalate input, .escalate textarea {
  font-size: 14px; border: 1px solid var(--dts-border); border-radius: 10px;
  padding: 9px 11px; width: 100%; color: var(--dts-fg); background: #fff;
}
.escalate textarea { resize: vertical; min-height: 56px; line-height: 1.4; }
.escalate input:focus, .escalate textarea:focus { outline: none; border-color: var(--dts-accent); }
.escalate .submit {
  background: var(--dts-accent); color: var(--dts-accent-text); border: none; border-radius: 10px;
  padding: 10px; cursor: pointer; font-size: 14px; font-weight: 600; margin-top: 2px;
}
.escalate .submit:disabled { opacity: .6; cursor: default; }
.escalate .err { color: #b91c1c; font-size: 12px; }
.escalate .note { color: var(--dts-muted); font-size: 12px; }
.escalate.done { align-items: center; text-align: center; gap: 6px; }
.escalate .check { color: #16a34a; }
.escalate .check svg { width: 38px; height: 38px; }

/* ── Mobile ──────────────────────────────────────── */
@media (max-width: 480px) {
  .panel {
    bottom: 0; right: 0; left: 0; width: 100%;
    height: 100%; max-width: 100%; max-height: 100%; border-radius: 0;
  }
  /* Already near-fullscreen on mobile — expanding is a no-op, not a shrink. */
  .panel.expanded { width: 100%; height: 100%; }
  .launcher { bottom: 16px; right: 16px; }
}

@media (prefers-reduced-motion: reduce) {
  .panel, .launcher, .typing span { transition: none; animation: none; }
}

/* ── Product cards ───────────────────────────────── */
.cards { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
.card {
  display: flex; gap: 10px; align-items: center; cursor: pointer;
  background: #fff; border: 1px solid var(--dts-border); border-radius: 12px; padding: 9px;
  transition: box-shadow .12s ease, border-color .12s ease;
}
.card:hover { border-color: #d1d5db; box-shadow: 0 2px 10px rgba(15,23,42,.07); }
.card:hover .card-name { text-decoration: underline; }  /* signals: clicking opens the product page */
.card-thumb {
  width: 52px; height: 52px; flex: 0 0 auto; border-radius: 8px; overflow: hidden;
  background: var(--dts-bot); display: flex; align-items: center; justify-content: center;
}
.card-thumb img { width: 100%; height: 100%; object-fit: cover; }
.card-info { flex: 1; min-width: 0; }
.card-name { font-size: 13px; font-weight: 600; color: var(--dts-fg); }
.card-price { font-size: 12px; color: var(--dts-muted); margin-top: 2px; }
.card-sale { color: var(--dts-accent); font-weight: 600; }
.list-price { color: var(--dts-muted); text-decoration: line-through; }
.card-actions { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.card-view { font-size: 12px; color: var(--dts-accent); display: inline-flex; align-items: center; gap: 3px; text-decoration: none; }
.card-view svg { width: 14px; height: 14px; }
.card-add {
  margin-left: auto; display: inline-flex; align-items: center; gap: 5px; cursor: pointer;
  font-size: 12px; font-weight: 600; padding: 7px 11px; border: none; border-radius: 8px;
  background: var(--dts-accent); color: var(--dts-accent-text);
}
.card-add svg { width: 15px; height: 15px; }
.more-options-btn {
  align-self: flex-start; margin-top: 2px;
  font-size: 13px; padding: 8px 13px; border-radius: 999px;
  border: 1px solid var(--dts-accent); background: transparent; color: var(--dts-accent);
  cursor: pointer; transition: background .12s ease, color .12s ease;
}
.more-options-btn:hover { background: var(--dts-accent); color: var(--dts-accent-text); }
.agent-offer-btn {
  display: block; margin: 8px 0 2px;
  font-size: 13px; font-weight: 600; padding: 9px 14px; border-radius: 999px;
  border: 1px solid var(--dts-accent); background: var(--dts-accent); color: var(--dts-accent-text);
  cursor: pointer; transition: opacity .12s ease;
}
.agent-offer-btn:hover { opacity: .88; }

/* ── Case/quantity popup ─────────────────────────── */
.pcard-overlay {
  position: absolute; inset: 0; z-index: 5;
  background: rgba(15,23,42,.5); display: flex; align-items: center; justify-content: center; padding: 16px;
}
.pcard {
  background: #fff; border-radius: 12px; padding: 16px; width: 100%; max-width: 300px;
  box-shadow: 0 12px 34px rgba(15,23,42,.28); max-height: 100%; overflow-y: auto;
}
.pcard-head { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 12px; }
.pcard-title { font-size: 14px; font-weight: 600; color: var(--dts-fg); flex: 1; line-height: 1.3; }
.pcard-close { border: none; background: none; cursor: pointer; color: var(--dts-muted); padding: 0; }
.pcard-close svg { width: 18px; height: 18px; }
.pcard-choose { font-size: 12px; color: var(--dts-muted); margin-bottom: 8px; }
.pcard-variants { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
.pcard-variant {
  display: flex; justify-content: space-between; align-items: center; cursor: pointer;
  border: 1px solid var(--dts-border); border-radius: 8px; padding: 9px 11px;
}
.pcard-variant.selected { border: 2px solid var(--dts-accent); background: rgba(156,7,7,.05); }
.pcard-vlabel { font-size: 13px; color: var(--dts-fg); }
.pcard-vprice { font-size: 13px; color: var(--dts-muted); }
.pcard-variant.selected .pcard-vprice, .pcard-variant.selected .pcard-vlabel { color: var(--dts-accent); font-weight: 600; }
.pcard-qty { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.pcard-qlabel { font-size: 13px; color: var(--dts-muted); }
.pcard-stepper { display: flex; align-items: center; border: 1px solid var(--dts-border); border-radius: 8px; overflow: hidden; }
.pcard-step { width: 32px; height: 32px; border: none; background: #fff; cursor: pointer; color: var(--dts-muted); display: flex; align-items: center; justify-content: center; }
.pcard-step svg { width: 16px; height: 16px; }
.pcard-qval { width: 36px; text-align: center; font-size: 13px; font-weight: 600; color: var(--dts-fg); border-left: 1px solid var(--dts-border); border-right: 1px solid var(--dts-border); line-height: 32px; }
.pcard-add {
  width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer;
  font-size: 14px; font-weight: 600; padding: 11px; border: none; border-radius: 8px;
  background: var(--dts-accent); color: var(--dts-accent-text);
}
.pcard-add:disabled { opacity: .7; cursor: default; }
.pcard-add svg { width: 17px; height: 17px; }
.pcard.done { text-align: center; }
.pcard-check { color: #16a34a; display: inline-flex; }
.pcard-check svg { width: 34px; height: 34px; }
.pcard-doneactions { display: flex; gap: 8px; justify-content: center; margin-top: 12px; }
.pcard-viewcart { font-size: 13px; font-weight: 600; color: var(--dts-accent-text); background: var(--dts-accent); padding: 9px 14px; border-radius: 8px; text-decoration: none; }
.pcard-keep { font-size: 13px; color: var(--dts-muted); background: none; border: 1px solid var(--dts-border); border-radius: 8px; padding: 9px 14px; cursor: pointer; }
.pcard-err { font-size: 12px; color: #b91c1c; margin-top: 10px; }
.pcard-openpage { color: var(--dts-accent); }
`;
}
